/* Dog quiz (sections/pet-quiz.liquid)
   - steps 1–5 → loader → results, with a fade/slide between steps
   - scoring: each selected answer adds 1 qualification per product in its
     data-products; rank by qualifications, ties by best-selling order
   - the best-selling product list is prefetched from the quiz-products
     section so the loader only waits for its minimum time */

if (!customElements.get('quiz-flow')) {
  customElements.define(
    'quiz-flow',
    class QuizFlow extends HTMLElement {
      connectedCallback() {
        this.steps = Array.from(this.querySelectorAll('.quiz-step'));
        this.questionCount = this.steps.filter((s) => /^\d+$/.test(s.dataset.step)).length;
        this.progress = this.querySelector('[data-progress]');
        this.progressBar = this.querySelector('[data-progress-bar]');
        this.nameInput = this.querySelector('[data-dog-name]');
        this.resultsWrap = this.querySelector('[data-results]');
        this.cardTemplate = this.querySelector('[data-card-template]');
        this.resultsCount = Number(this.dataset.results) || 3;
        this.loaderMin = Number(this.dataset.loaderMin) || 3000;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.current = this.steps.find((s) => s.classList.contains('is-active')) || this.steps[0];
        this.animating = false;

        // products in best-selling order — kick off now so results are instant later
        this.productsPromise = fetch(this.dataset.productsUrl) // Section Rendering API 404s when Accept is application/json
          .then((r) => r.json())
          .then((json) => {
            const doc = new DOMParser().parseFromString(json['quiz-products'], 'text/html');
            return JSON.parse(doc.querySelector('[data-quiz-products]').textContent);
          })
          .catch(() => []);

        this.bind();
      }

      bind() {
        // step 1: age tiles advance on click
        this.querySelectorAll('.quiz-age').forEach((tile) => {
          tile.addEventListener('click', () => {
            this.querySelectorAll('.quiz-age').forEach((t) => {
              const on = t === tile;
              t.classList.toggle('is-active', on);
              t.setAttribute('aria-pressed', String(on));
            });
            this.age = tile.dataset.answer;
            setTimeout(() => this.go(this.stepAfter(this.current)), this.reduceMotion ? 0 : 220);
          });
        });

        // name → loader copy + results title
        if (this.nameInput) {
          this.nameInput.addEventListener('input', () => this.applyName());
          this.nameInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); this.go(this.stepAfter(this.current)); }
          });
        }

        // Back / Next / Finish
        this.querySelectorAll('[data-back]').forEach((b) => b.addEventListener('click', () => this.go(this.stepBefore(this.current), true)));
        this.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => this.go(this.stepAfter(this.current))));
        this.querySelectorAll('[data-finish]').forEach((b) => b.addEventListener('click', () => this.finish()));

        // single-select steps need an answer before Next enables
        this.steps.forEach((step) => {
          const next = step.querySelector('[data-requires-choice]');
          if (!next) return;
          step.addEventListener('change', () => {
            next.disabled = !step.querySelector('input:checked');
          });
        });
      }

      /* ---------- navigation ---------- */
      stepAfter(step) { return this.steps[this.steps.indexOf(step) + 1]; }
      stepBefore(step) { return this.steps[Math.max(0, this.steps.indexOf(step) - 1)]; }

      go(target, backwards = false) {
        if (!target || target === this.current || this.animating) return;
        const from = this.current;
        this.animating = true;
        this.current = target;

        const finishSwap = () => {
          from.hidden = true;
          from.classList.remove('is-active', 'is-leaving');
          target.hidden = false;
          target.classList.add('is-active', backwards ? 'is-entering-back' : 'is-entering');
          this.updateProgress(target);
          requestAnimationFrame(() => requestAnimationFrame(() => {
            target.classList.remove('is-entering', 'is-entering-back');
            const heading = target.querySelector('.quiz-step__title');
            if (heading) heading.focus({ preventScroll: true });
            this.scrollIntoView({ block: 'start', behavior: this.reduceMotion ? 'auto' : 'smooth' });
            this.animating = false;
          }));
        };

        if (this.reduceMotion) { finishSwap(); return; }
        from.classList.add('is-leaving');
        from.addEventListener('transitionend', finishSwap, { once: true });
        setTimeout(() => { if (this.animating && from.classList.contains('is-leaving')) finishSwap(); }, 400);
      }

      updateProgress(step) {
        const n = Number(step.dataset.step);
        const done = Number.isFinite(n) ? n - 1 : this.questionCount;
        const isQuestion = Number.isFinite(n);
        this.progress.hidden = !isQuestion;
        this.progress.setAttribute('aria-valuenow', String(done));
        this.progressBar.style.width = `${(done / this.questionCount) * 100}%`;
      }

      /* ---------- name ---------- */
      applyName() {
        const name = (this.nameInput.value || '').trim();
        const loader = this.querySelector('[data-loader-copy]');
        const title = this.querySelector('[data-results-title]');
        if (loader) loader.textContent = name ? loader.dataset.template.replace('{name}', name) : 'Hold tight...we\'re putting together the right plan for your pet.';
        if (title) title.innerHTML = name ? title.dataset.template.replace('{name}', this.escape(name).toUpperCase()) : 'PET\'S VET IQ<sup class="reg">®</sup> PLAN';
      }

      escape(text) {
        return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      }

      /* ---------- scoring ---------- */
      scores() {
        const tally = {};
        this.querySelectorAll('.quiz-step input:checked[data-products]').forEach((input) => {
          input.dataset.products.split(',').forEach((handle) => {
            handle = handle.trim();
            if (handle) tally[handle] = (tally[handle] || 0) + 1;
          });
        });
        return tally;
      }

      async finish() {
        const loader = this.steps.find((s) => s.dataset.step === 'loader');
        const results = this.steps.find((s) => s.dataset.step === 'results');
        const started = Date.now();
        this.go(loader);

        const products = await this.productsPromise;           // best-selling order
        const tally = this.scores();
        const rank = new Map(products.map((p, i) => [p.handle, i]));
        const picks = products
          .filter((p) => p.available !== false)
          .map((p) => ({ ...p, score: tally[p.handle] || 0, order: rank.get(p.handle) }))
          .sort((a, b) => b.score - a.score || a.order - b.order)   // rule 1: qualifications, rule 2: best sellers
          .slice(0, this.resultsCount);
        this.renderResults(picks);

        const wait = Math.max(0, this.loaderMin - (Date.now() - started));
        setTimeout(() => this.go(results), wait);
      }

      renderResults(picks) {
        this.resultsWrap.replaceChildren();
        picks.forEach((p) => {
          const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
          const img = card.querySelector('img');
          img.src = p.image;
          img.alt = p.title;
          card.querySelector('h3').textContent = p.title;
          const link = card.querySelector('a');
          link.href = p.url;
          link.querySelector('.visually-hidden').textContent = ` about ${p.title}`;
          this.resultsWrap.appendChild(card);
        });
      }
    }
  );
}
